import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Alert, Button, Form, Modal, Spinner } from "react-bootstrap";
import { FaDownload, FaFilePdf, FaTrash, FaUpload } from "react-icons/fa";
import { toast } from "react-toastify";
import {
  useDeleteDirectoryDocumentMutation,
  useGetDirectoryDocumentQuery,
  useUploadDirectoryDocumentMutation,
} from "../slices/directoryDocumentApiSlice";
import downloadDirectoryPdf from "../utils/downloadDirectoryPdf";

const MAX_PDF_SIZE = 25 * 1024 * 1024;

const formatFileSize = (bytes) => {
  if (!Number.isFinite(bytes) || bytes <= 0) return "Unknown size";
  if (bytes < 1024 * 1024) return `${Math.ceil(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const DirectoryPdfManagerModal = ({ show, onHide }) => {
  const fileInputRef = useRef(null);
  const [title, setTitle] = useState("Membership Directory");
  const [selectedFile, setSelectedFile] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  const {
    data,
    isLoading: isLoadingDocument,
    error: documentError,
    refetch,
  } = useGetDirectoryDocumentQuery(undefined, { skip: !show });
  const [uploadDocument, { isLoading: isUploading }] =
    useUploadDirectoryDocumentMutation();
  const [deleteDocument, { isLoading: isDeleting }] =
    useDeleteDirectoryDocumentMutation();

  const document = data?.document;
  const isBusy = isUploading || isDeleting;

  useEffect(() => {
    if (show) {
      setTitle(document?.title || "Membership Directory");
      setSelectedFile(null);
      setConfirmingDelete(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [show, document?.title]);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    if (!isPdf) {
      toast.error("Please select a PDF file");
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    if (file.size > MAX_PDF_SIZE) {
      toast.error("The PDF must be 25 MB or smaller");
      event.target.value = "";
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!selectedFile) {
      toast.error("Please select a PDF to upload");
      return;
    }

    const formData = new FormData();
    formData.append("title", title.trim() || "Membership Directory");
    formData.append("pdf", selectedFile);

    try {
      await uploadDocument(formData).unwrap();
      toast.success(
        document ? "Directory PDF replaced" : "Directory PDF uploaded"
      );
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to upload directory PDF");
    }
  };

  const handleDelete = async () => {
    try {
      await deleteDocument().unwrap();
      toast.success("Directory PDF removed");
      setConfirmingDelete(false);
      setTitle("Membership Directory");
      refetch();
    } catch (error) {
      toast.error(error?.data?.message || "Failed to remove directory PDF");
    }
  };

  const handleDownload = async () => {
    if (!document) return;

    setIsDownloading(true);
    try {
      await downloadDirectoryPdf(document);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Modal show={show} onHide={isBusy ? undefined : onHide} centered size="lg">
      <Modal.Header closeButton={!isBusy}>
        <Modal.Title>
          <FaFilePdf className="me-2 text-danger" />
          Manage Directory PDF
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoadingDocument ? (
          <div className="text-center py-4">
            <Spinner animation="border" role="status" />
          </div>
        ) : documentError ? (
          <Alert variant="danger">
            {documentError?.data?.message || "Unable to load the directory PDF"}
          </Alert>
        ) : (
          <>
            {document && (
              <div className="directory-pdf-current rounded-3 p-3 mb-4">
                <div className="d-flex flex-column flex-md-row justify-content-between gap-3">
                  <div>
                    <div className="text-uppercase small fw-semibold text-muted mb-1">
                      Current PDF
                    </div>
                    <h5 className="mb-1">{document.title}</h5>
                    <div className="text-muted small">
                      {document.originalName} | {formatFileSize(document.size)} | Uploaded{" "}
                      {new Date(document.uploadedAt).toLocaleDateString()}
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-primary"
                      onClick={handleDownload}
                      disabled={isDownloading}
                    >
                      <FaDownload className="me-2" />
                      {isDownloading ? "Downloading..." : "Download"}
                    </Button>
                    <Button
                      variant="outline-danger"
                      onClick={() => setConfirmingDelete(true)}
                      disabled={isBusy}
                    >
                      <FaTrash />
                    </Button>
                  </div>
                </div>
              </div>
            )}

            {confirmingDelete && (
              <Alert variant="warning">
                <div className="fw-semibold mb-2">
                  Remove the current directory PDF?
                </div>
                <div className="mb-3">
                  Members will no longer see a download option until another
                  PDF is uploaded.
                </div>
                <div className="d-flex gap-2">
                  <Button
                    variant="danger"
                    onClick={handleDelete}
                    disabled={isDeleting}
                  >
                    {isDeleting ? "Removing..." : "Remove PDF"}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={isDeleting}
                  >
                    Cancel
                  </Button>
                </div>
              </Alert>
            )}

            <Form onSubmit={handleUpload}>
              <Form.Group className="mb-3" controlId="directoryPdfTitle">
                <Form.Label>Display title</Form.Label>
                <Form.Control
                  type="text"
                  value={title}
                  maxLength={120}
                  onChange={(event) => setTitle(event.target.value)}
                  placeholder="Membership Directory"
                />
              </Form.Group>
              <Form.Group className="mb-3" controlId="directoryPdfFile">
                <Form.Label>
                  {document ? "Replacement PDF" : "Directory PDF"}
                </Form.Label>
                <Form.Control
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,.pdf"
                  onChange={handleFileChange}
                  disabled={isBusy}
                />
                <Form.Text className="text-muted">PDF only, maximum 25 MB.</Form.Text>
              </Form.Group>
              <Button
                type="submit"
                variant="primary"
                disabled={!selectedFile || isBusy}
              >
                {isUploading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <FaUpload className="me-2" />
                    {document ? "Replace PDF" : "Upload PDF"}
                  </>
                )}
              </Button>
            </Form>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide} disabled={isBusy}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

DirectoryPdfManagerModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
};

export default DirectoryPdfManagerModal;
